import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import fetchWrapper from '@/services/fetchWrapper.service';

import {
  getSearchRandomizedFeedState,
  SearchRandomizedFeedsContext,
} from './searchRandomizedFeeds.context';

export default function SearchRandomizedFeedsProvider({ children }) {
  const [feeds, setFeeds] = useState({});
  const feedsRef = useRef({});
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
    };
  }, []);

  const setFeed = useCallback((feedKey, nextFeed) => {
    const nextFeeds = {
      ...feedsRef.current,
      [feedKey]: nextFeed,
    };

    feedsRef.current = nextFeeds;
    setFeeds(nextFeeds);
  }, []);

  const loadFeed = useCallback(async ({
    feedKey,
    endpoint,
    limitParam,
    limit,
    injectToken = false,
    optionalToken = true,
    errorMessage,
  }) => {
    const existingFeed = getSearchRandomizedFeedState(feedsRef.current, feedKey);
    if (existingFeed.hasRequested) {
      return;
    }

    setFeed(feedKey, {
      hasRequested: true,
      loading: true,
      data: [],
      error: null,
    });

    try {
      const { response, data } = await fetchWrapper.get(
        `${endpoint}?${limitParam}=${limit}`,
        { injectToken, optionalToken }
      );

      if (!mountedRef.current) {
        return;
      }

      if (response.ok && Array.isArray(data)) {
        setFeed(feedKey, {
          hasRequested: true,
          loading: false,
          data,
          error: null,
        });
        return;
      }

      setFeed(feedKey, {
        hasRequested: true,
        loading: false,
        data: Array.isArray(data) ? data : [],
        error: errorMessage,
      });
    } catch (error) {
      if (!mountedRef.current) {
        return;
      }

      setFeed(feedKey, {
        hasRequested: true,
        loading: false,
        data: [],
        error: error?.message || errorMessage,
      });
    }
  }, [setFeed]);

  const value = useMemo(() => ({ feeds, loadFeed }), [feeds, loadFeed]);

  return (
    <SearchRandomizedFeedsContext.Provider value={value}>
      {children}
    </SearchRandomizedFeedsContext.Provider>
  );
}
